import React, { useRef, useMemo, useState, useEffect, Suspense } from "react";
import { useFrame } from "@react-three/fiber";
import { RoundedBox, useTexture } from "@react-three/drei";
import * as THREE from "three";
import { BRAND } from "../lib/brand";
import { useNight } from "../lib/worldTheme";

const DRILLDOWN_LOGO = "/drilldown-logo.png";
const SM_BUG_LOGO = "/sm-bug-logo.svg";
const SM_LOGO_ASPECT = 34.06 / 28.48; // height / width of the bug logo artwork

// Rasterise the Simon/Myers bug SVG into a crisp, transparent CanvasTexture so it
// can be mapped onto a 3D plane (browsers — incl. headless Chromium — render SVG
// into an <img>, which we then draw onto a high-res canvas).
function useSvgTexture(url, w = 512) {
  const [tex, setTex] = useState(null);
  useEffect(() => {
    let disposed = false;
    const img = new Image();
    img.onload = () => {
      if (disposed) return;
      const cw = w;
      const ch = Math.round(w * SM_LOGO_ASPECT);
      const canvas = document.createElement("canvas");
      canvas.width = cw;
      canvas.height = ch;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, cw, ch);
      ctx.drawImage(img, 0, 0, cw, ch);
      const t = new THREE.CanvasTexture(canvas);
      t.colorSpace = THREE.SRGBColorSpace;
      t.anisotropy = 8;
      t.minFilter = THREE.LinearFilter;
      t.magFilter = THREE.LinearFilter;
      t.needsUpdate = true;
      setTex(t);
    };
    img.src = url;
    return () => { disposed = true; };
  }, [url, w]);
  return tex;
}

// The S/M bug logo as a charcoal brand medallion that spins on top of the Beacon.
// The yellow rod is the "stick" it turns on; the medallion shows the logo on both
// faces so it reads from every angle as it rotates.
function SpinningLogo({ y, size = 1.7 }) {
  const tex = useSvgTexture(SM_BUG_LOGO, 512);
  const spin = useRef();
  useFrame((s) => { if (spin.current) spin.current.rotation.y = s.clock.elapsedTime * 0.9; });
  const W = size;
  const H = size * SM_LOGO_ASPECT;
  return (
    <group position={[0, y, 0]}>
      {/* yellow stick the medallion turns on */}
      <mesh position={[0, -0.1, 0]}>
        <cylinderGeometry args={[0.07, 0.07, H + 1.0, 12]} />
        <meshStandardMaterial color={BRAND.yellow} emissive={BRAND.yellow} emissiveIntensity={0.55} metalness={0.45} roughness={0.3} />
      </mesh>
      <group ref={spin}>
        {/* charcoal medallion body */}
        <RoundedBox args={[W * 1.18, H * 1.12, 0.16]} radius={0.08} smoothness={3} castShadow>
          <meshStandardMaterial color={BRAND.charcoal} roughness={0.5} metalness={0.25} />
        </RoundedBox>
        {tex && (
          <group>
            <mesh position={[0, 0, 0.085]}>
              <planeGeometry args={[W, H]} />
              <meshStandardMaterial map={tex} transparent alphaTest={0.08} emissiveMap={tex} emissive="#ffffff" emissiveIntensity={0.45} roughness={0.6} polygonOffset polygonOffsetFactor={-2} polygonOffsetUnits={-2} />
            </mesh>
            <mesh position={[0, 0, -0.085]} rotation={[0, Math.PI, 0]}>
              <planeGeometry args={[W, H]} />
              <meshStandardMaterial map={tex} transparent alphaTest={0.08} emissiveMap={tex} emissive="#ffffff" emissiveIntensity={0.45} roughness={0.6} polygonOffset polygonOffsetFactor={-2} polygonOffsetUnits={-2} />
            </mesh>
          </group>
        )}
      </group>
    </group>
  );
}

// (The Drill Down sign now lives flat on the kiosk walls — see DrillDownWallLogo below.)

// THE BEACON — live "send a signal" layer for the modern twisting tower:
// a pulsing lantern-core, expanding signal rings, a soft upward light shaft and a
// blinking top light. Geometry constants mirror the beacon branch in Building.js.
function AnimatedBeacon({ h, w, accent }) {
  const night = useNight();
  const orb = useRef();
  const ring1 = useRef();
  const ring2 = useRef();
  const ring3 = useRef();
  const towerH = h * 0.66;
  const topW = w * 0.5;
  const lensH = w * 1.2;
  const lightY = towerH + 0.3 + lensH / 2;
  const topY = lightY + lensH / 2 + 0.5 + topW * 0.9; // cap tip
  const logoSize = 1.7;
  const logoY = topY + 0.45 + (logoSize * SM_LOGO_ASPECT) / 2;
  const rings = [ring1, ring2, ring3];
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (orb.current) {
      const p = (Math.sin(t * 2.2) * 0.5 + 0.5);
      orb.current.material.emissiveIntensity = (night ? 1.6 : 1.2) + p * 0.9;
      orb.current.scale.setScalar(1 + p * 0.07);
    }
    for (let i = 0; i < 3; i++) {
      const r = rings[i].current;
      if (!r) continue;
      const phase = (t * 0.45 + i / 3) % 1;       // 0..1 staggered
      const sc = 0.5 + phase * 2.8;
      r.scale.set(sc, sc, sc);
      r.material.opacity = 0.5 * (1 - phase);
    }
  });
  return (
    <group>
      {/* pulsing core glow inside the lantern lens */}
      <mesh ref={orb} position={[0, lightY, 0]}>
        <sphereGeometry args={[topW * 0.3, 16, 12]} />
        <meshStandardMaterial color={accent} emissive={accent} emissiveIntensity={1.4} toneMapped={false} roughness={0.2} />
      </mesh>
      {/* expanding horizontal "send a signal" rings */}
      {rings.map((ref, i) => (
        <mesh key={i} ref={ref} position={[0, lightY, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[topW * 0.9, 0.05, 8, 48]} />
          <meshBasicMaterial color={accent} transparent opacity={0.4} depthWrite={false} />
        </mesh>
      ))}
      {/* the S/M bug logo medallion spinning on its yellow stick, crowning the tower */}
      <SpinningLogo y={logoY} size={logoSize} />
      {/* warm light cast by the beacon */}
      <pointLight position={[0, lightY, 0]} color={"#FFE9A8"} intensity={night ? 2.2 : 1.2} distance={w * 9} decay={2} />
    </group>
  );
}

function AnimatedDrill({ y, accent }) {
  const drill = useRef();
  useFrame((state) => {
    if (drill.current) drill.current.rotation.y = state.clock.elapsedTime * 2.2;
  });
  return (
    <group position={[0, y, 0]}>
      {/* mount */}
      <mesh position={[0, -0.2, 0]}><cylinderGeometry args={[0.5, 0.6, 0.4, 12]} /><meshStandardMaterial color={BRAND.charcoal} metalness={0.4} roughness={0.4} /></mesh>
      <group ref={drill}>
        {/* shaft */}
        <mesh position={[0, 0.7, 0]}><cylinderGeometry args={[0.22, 0.22, 1.4, 12]} /><meshStandardMaterial color={"#B9BCC2"} metalness={0.6} roughness={0.3} /></mesh>
        {/* helical flutes */}
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <mesh key={i} position={[0, 0.4 + i * 0.18, 0]} rotation={[0, (i * Math.PI) / 3, Math.PI / 5]}>
            <boxGeometry args={[0.7, 0.06, 0.16]} />
            <meshStandardMaterial color={accent} metalness={0.4} roughness={0.3} />
          </mesh>
        ))}
        {/* tip */}
        <mesh position={[0, 1.6, 0]}><coneGeometry args={[0.22, 0.5, 12]} /><meshStandardMaterial color={"#D9DCE2"} metalness={0.7} roughness={0.2} /></mesh>
      </group>
    </group>
  );
}

function Ticker({ w, y, d }) {
  const night = useNight();
  return (
    <mesh position={[0, y, d / 2 + 0.06]}>
      <boxGeometry args={[w * 0.96, 0.55, 0.08]} />
      <meshStandardMaterial color={BRAND.charcoal} emissive={BRAND.yellow} emissiveIntensity={night ? 0.55 : 0.28} />
    </mesh>
  );
}

function PulsePanel({ position, args, color, base = 0.35, amp = 0.55, speed = 2 }) {
  const ref = useRef();
  useFrame((s) => {
    if (ref.current) ref.current.material.emissiveIntensity = base + (Math.sin(s.clock.elapsedTime * speed) * 0.5 + 0.5) * amp;
  });
  return (
    <mesh ref={ref} position={position}>
      <planeGeometry args={args} />
      <meshStandardMaterial color={color} emissive={color} emissiveIntensity={base} />
    </mesh>
  );
}

function AcUnit({ position, s = 1 }) {
  return (
    <RoundedBox args={[0.9 * s, 0.5 * s, 0.9 * s]} radius={0.05} smoothness={2} position={position} castShadow>
      <meshStandardMaterial color={"#9A9A93"} roughness={0.7} metalness={0.2} />
    </RoundedBox>
  );
}

function Dish({ position }) {
  return (
    <group position={position} rotation={[Math.PI / 6, Math.PI / 5, 0]}>
      <mesh><sphereGeometry args={[0.55, 12, 12, 0, Math.PI * 2, 0, Math.PI / 2.4]} /><meshStandardMaterial color={BRAND.white} roughness={0.6} side={THREE.DoubleSide} /></mesh>
      <mesh position={[0, 0.3, 0]}><cylinderGeometry args={[0.03, 0.03, 0.6, 6]} /><meshStandardMaterial color={BRAND.charcoal} /></mesh>
    </group>
  );
}

function Flag({ position, color }) {
  const flag = useRef();
  useFrame((s) => { if (flag.current) flag.current.rotation.y = Math.sin(s.clock.elapsedTime * 2 + position[0]) * 0.25; });
  return (
    <group position={position}>
      <mesh position={[0, 0.7, 0]}><cylinderGeometry args={[0.04, 0.04, 1.6, 6]} /><meshStandardMaterial color={BRAND.charcoal} /></mesh>
      <group ref={flag} position={[0, 1.3, 0]}>
        <mesh position={[0.35, 0, 0]}><boxGeometry args={[0.7, 0.45, 0.04]} /><meshStandardMaterial color={color} roughness={0.5} side={THREE.DoubleSide} /></mesh>
      </group>
    </group>
  );
}

// The Drill Down logo mounted flat on the kiosk's visible white walls.
function DrillDownWallLogo({ w, d, h }) {
  const tex = useTexture(DRILLDOWN_LOGO);
  useMemo(() => {
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 8;
    tex.needsUpdate = true;
    return tex;
  }, [tex]);
  const W = 2.4;
  const H = W * (266 / 207);
  const y = h * 0.42;
  // The door now lives on the RIGHT (+X) wall, so the sign moves to the LEFT (+Z) wall.
  const walls = [
    { pos: [0, y, d / 2 + 0.06], ry: 0 },
  ];
  return (
    <group>
      {walls.map((wall, i) => (
        <group key={i} position={wall.pos} rotation={[0, wall.ry, 0]}>
          {/* thin charcoal frame */}
          <mesh>
            <planeGeometry args={[W + 0.22, H + 0.22]} />
            <meshStandardMaterial color={BRAND.charcoal} roughness={0.6} polygonOffset polygonOffsetFactor={-1} polygonOffsetUnits={-1} />
          </mesh>
          {/* bright white card so the sign stays readable under the canopy shadow */}
          <mesh position={[0, 0, 0.012]}>
            <planeGeometry args={[W + 0.04, H + 0.04]} />
            <meshStandardMaterial color="#FFFFFF" emissive="#FFFFFF" emissiveIntensity={0.5} roughness={0.7} polygonOffset polygonOffsetFactor={-3} polygonOffsetUnits={-3} />
          </mesh>
          {/* the logo artwork */}
          <mesh position={[0, 0, 0.024]}>
            <planeGeometry args={[W, H]} />
            <meshStandardMaterial map={tex} transparent alphaTest={0.02} roughness={0.8} emissiveMap={tex} emissive="#ffffff" emissiveIntensity={0.55} polygonOffset polygonOffsetFactor={-5} polygonOffsetUnits={-5} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

// Corporate window grid for the Drill Down — on the RIGHT (+X) wall and on the
// FRONT (-Z) entrance wall (the pavement side, beside the door) so no facade is bare.
function DrillDownWindows({ w, d, h }) {
  const night = useNight();
  const cols = 3;
  const rows = 2;
  const cw = 0.86;
  const ch = 0.68;
  const gapx = 0.4;
  const gapy = 0.38;

  const cell = (key, pos, ry) => (
    <group key={key} position={pos} rotation={[0, ry, 0]}>
      <mesh>
        <planeGeometry args={[cw + 0.12, ch + 0.12]} />
        <meshStandardMaterial color={BRAND.charcoal} roughness={0.6} polygonOffset polygonOffsetFactor={-2} polygonOffsetUnits={-2} />
      </mesh>
      <mesh position={[0, 0, 0.02]}>
        <planeGeometry args={[cw, ch]} />
        <meshStandardMaterial color={"#8FB4C8"} emissive={"#8FB4C8"} emissiveIntensity={night ? 0.45 : 0.08} roughness={0.15} metalness={0.45} polygonOffset polygonOffsetFactor={-3} polygonOffsetUnits={-3} />
      </mesh>
      <mesh position={[0, 0, 0.03]}><boxGeometry args={[cw, 0.04, 0.02]} /><meshStandardMaterial color={BRAND.charcoal} /></mesh>
      <mesh position={[0, 0, 0.03]}><boxGeometry args={[0.04, ch, 0.02]} /><meshStandardMaterial color={BRAND.charcoal} /></mesh>
    </group>
  );

  const items = [];
  for (let c = 0; c < cols; c++) {
    for (let rr = 0; rr < rows; rr++) {
      const off = (c - (cols - 1) / 2) * (cw + gapx);
      const yy = h * 0.5 + rr * (ch + gapy);
      // RIGHT (+X) wall
      items.push(cell(`px-${c}-${rr}`, [w / 2 + 0.05, yy, off], Math.PI / 2));
      // FRONT (-Z) wall — the entrance/pavement side (windows sit above the door)
      items.push(cell(`nz-${c}-${rr}`, [off, yy, -(d / 2 + 0.05)], Math.PI));
    }
  }
  return <group>{items}</group>;
}

// (The experiential facade is now a seamless video on the building body itself —
// see ExperientialBody in Building.js. No extra detail component is needed here.)

// A projecting, glowing NEON SIGN that cantilevers off the Marketing tower —
// Chinatown-night-market style. Each sign gently sways on its support arm while its
// neon pulses with an occasional flicker. The sign face is built perpendicular to
// the arm (facing local +Z) and varies by `shape` for an abstract, eclectic skyline.
function NeonSign({ angle, anchorY, wallX, proj, color, shape, scale = 1 }) {
  // Static signs — they glow steadily, no sway / pulse / flicker.
  const glowRef = useRef();

  const neon = (extra = {}) => (
    <meshStandardMaterial color={color} emissive={color} emissiveIntensity={1.3} toneMapped={false} {...extra} />
  );
  const back = (w, hh) => (
    <mesh position={[0, 0, -0.06]} castShadow>
      <boxGeometry args={[w, hh, 0.12]} />
      <meshStandardMaterial color={BRAND.charcoal} roughness={0.6} metalness={0.2} />
    </mesh>
  );

  let face = null;
  if (shape === "rect") {
    const W = 1.5 * scale, H = 1.0 * scale, t = 0.08 * scale;
    face = (
      <group>
        {back(W + 0.18, H + 0.18)}
        <mesh ref={glowRef} position={[0, 0, 0.03]}><planeGeometry args={[W - 0.18, H - 0.18]} />{neon({ emissiveIntensity: 0.9 })}</mesh>
        <mesh position={[0, H / 2, 0.05]}><boxGeometry args={[W, t, 0.05]} />{neon()}</mesh>
        <mesh position={[0, -H / 2, 0.05]}><boxGeometry args={[W, t, 0.05]} />{neon()}</mesh>
        <mesh position={[-W / 2, 0, 0.05]}><boxGeometry args={[t, H, 0.05]} />{neon()}</mesh>
        <mesh position={[W / 2, 0, 0.05]}><boxGeometry args={[t, H, 0.05]} />{neon()}</mesh>
      </group>
    );
  } else if (shape === "ring") {
    const R = 0.72 * scale;
    face = (
      <group>
        <mesh position={[0, 0, -0.05]}><circleGeometry args={[R + 0.22, 28]} /><meshStandardMaterial color={BRAND.charcoal} roughness={0.6} metalness={0.2} /></mesh>
        <mesh ref={glowRef} position={[0, 0, 0.03]}><torusGeometry args={[R, 0.1, 12, 40]} />{neon()}</mesh>
        <mesh position={[0, 0, 0.03]}><circleGeometry args={[R * 0.32, 24]} />{neon({ emissiveIntensity: 1.0 })}</mesh>
      </group>
    );
  } else if (shape === "bars") {
    const W = 1.5 * scale, H = 1.15 * scale;
    const hs = [0.55, 0.85, 0.7, 1.0, 0.62];
    face = (
      <group>
        {back(W, H)}
        {hs.map((bh, i) => (
          <mesh key={i} ref={i === 3 ? glowRef : undefined} position={[(i - 2) * (W * 0.18), -H / 2 + (bh * H) / 2 + 0.06, 0.05]}>
            <boxGeometry args={[W * 0.1, bh * H * 0.92, 0.06]} />
            {neon({ emissiveIntensity: 1.0 + i * 0.06 })}
          </mesh>
        ))}
      </group>
    );
  } else if (shape === "blade") {
    const W = 0.72 * scale, H = 2.3 * scale;
    face = (
      <group>
        {back(W, H)}
        <mesh ref={glowRef} position={[0, 0, 0.03]}><planeGeometry args={[W - 0.22, H - 0.18]} />{neon({ emissiveIntensity: 0.85 })}</mesh>
        {[-0.62, -0.2, 0.22, 0.64].map((ty, i) => (
          <mesh key={i} position={[0, ty * H * 0.5, 0.06]}><boxGeometry args={[W - 0.16, 0.08, 0.05]} />{neon()}</mesh>
        ))}
      </group>
    );
  } else {
    // diamond
    const D = 1.0 * scale;
    face = (
      <group rotation={[0, 0, Math.PI / 4]}>
        {back(D, D)}
        <mesh ref={glowRef} position={[0, 0, 0.03]}><planeGeometry args={[D - 0.2, D - 0.2]} />{neon({ emissiveIntensity: 0.9 })}</mesh>
        <mesh position={[0, D / 2, 0.05]}><boxGeometry args={[D, 0.08, 0.05]} />{neon()}</mesh>
        <mesh position={[0, -D / 2, 0.05]}><boxGeometry args={[D, 0.08, 0.05]} />{neon()}</mesh>
        <mesh position={[-D / 2, 0, 0.05]}><boxGeometry args={[0.08, D, 0.05]} />{neon()}</mesh>
        <mesh position={[D / 2, 0, 0.05]}><boxGeometry args={[0.08, D, 0.05]} />{neon()}</mesh>
      </group>
    );
  }

  return (
    <group position={[0, anchorY, 0]} rotation={[0, angle, 0]}>
      <group>
        {/* wall mount + support arm projecting outward (+X) */}
        <mesh position={[wallX + 0.02, 0, 0]}><boxGeometry args={[0.18, 0.6, 0.6]} /><meshStandardMaterial color={BRAND.charcoal} roughness={0.5} metalness={0.3} /></mesh>
        <mesh position={[wallX + proj / 2, 0.02, 0]} castShadow><boxGeometry args={[proj, 0.1, 0.1]} /><meshStandardMaterial color={BRAND.charcoal} metalness={0.5} roughness={0.4} /></mesh>
        {/* two short hangers down to the sign */}
        <mesh position={[wallX + proj * 0.7, -0.18, 0.14]}><boxGeometry args={[0.05, 0.34, 0.05]} /><meshStandardMaterial color={BRAND.charcoal} /></mesh>
        <mesh position={[wallX + proj * 0.7, -0.18, -0.14]}><boxGeometry args={[0.05, 0.34, 0.05]} /><meshStandardMaterial color={BRAND.charcoal} /></mesh>
        {/* the glowing sign, hung just below the arm */}
        <group position={[wallX + proj, -0.42, 0]}>
          {face}
          {/* coloured neon glow cast onto the tower + air around the sign */}
          <pointLight color={color} intensity={1.4} distance={3.4} decay={2} />
        </group>
      </group>
    </group>
  );
}

// A simple glazed window grid (used to dress bare office facades).
function WindowGrid({ cols, rows, cw, ch, gx, gy, night, tint = "#BBD7E6" }) {
  const cells = [];
  for (let c = 0; c < cols; c++) {
    for (let r = 0; r < rows; r++) {
      const x = (c - (cols - 1) / 2) * (cw + gx);
      const y = (r - (rows - 1) / 2) * (ch + gy);
      const lit = night && (c * 3 + r * 5) % 3 !== 0;
      cells.push(
        <group key={`${c}-${r}`} position={[x, y, 0]}>
          <mesh>
            <planeGeometry args={[cw + 0.08, ch + 0.08]} />
            <meshStandardMaterial color={BRAND.charcoal} roughness={0.6} polygonOffset polygonOffsetFactor={-2} polygonOffsetUnits={-2} />
          </mesh>
          <mesh position={[0, 0, 0.02]}>
            <planeGeometry args={[cw, ch]} />
            <meshStandardMaterial color={lit ? BRAND.yellow : tint} emissive={lit ? BRAND.yellow : tint} emissiveIntensity={lit ? 0.8 : night ? 0.3 : 0.06} roughness={0.15} metalness={0.4} polygonOffset polygonOffsetFactor={-3} polygonOffsetUnits={-3} />
          </mesh>
        </group>
      );
    }
  }
  return <group>{cells}</group>;
}

// A glowing analytics "growth graph" screen — a rising yellow line over a grid,
// the data centrepiece for the Consulting (Strategy & Data) building.
function GrowthGraph({ width, height, accent = BRAND.yellow }) {
  const pts = [0.12, 0.3, 0.22, 0.5, 0.42, 0.72, 0.92];
  const n = pts.length;
  const px = (i) => (i / (n - 1) - 0.5) * width * 0.88;
  const py = (p) => (p - 0.5) * height * 0.7;
  const segs = [];
  for (let i = 0; i < n - 1; i++) {
    const x0 = px(i), x1 = px(i + 1);
    const y0 = py(pts[i]), y1 = py(pts[i + 1]);
    const len = Math.hypot(x1 - x0, y1 - y0);
    const ang = Math.atan2(y1 - y0, x1 - x0);
    segs.push(
      <mesh key={`l${i}`} position={[(x0 + x1) / 2, (y0 + y1) / 2, 0.05]} rotation={[0, 0, ang]}>
        <boxGeometry args={[len, 0.08, 0.04]} />
        <meshStandardMaterial color={accent} emissive={accent} emissiveIntensity={1.2} toneMapped={false} />
      </mesh>
    );
  }
  const dots = pts.map((p, i) => (
    <mesh key={`d${i}`} position={[px(i), py(p), 0.07]}>
      <sphereGeometry args={[0.075, 10, 10]} />
      <meshStandardMaterial color={"#ffffff"} emissive={accent} emissiveIntensity={0.9} toneMapped={false} />
    </mesh>
  ));
  const grid = [];
  for (let i = 1; i < 4; i++) {
    grid.push(
      <mesh key={`g${i}`} position={[0, (i / 4 - 0.5) * height * 0.7, 0.02]}>
        <boxGeometry args={[width * 0.9, 0.012, 0.02]} />
        <meshStandardMaterial color={"#3A5566"} />
      </mesh>
    );
  }
  return (
    <group>
      <mesh position={[0, 0, -0.02]}>
        <planeGeometry args={[width + 0.16, height + 0.16]} />
        <meshStandardMaterial color={BRAND.charcoal} roughness={0.6} />
      </mesh>
      <mesh>
        <planeGeometry args={[width, height]} />
        <meshStandardMaterial color={"#11202B"} roughness={0.4} metalness={0.2} polygonOffset polygonOffsetFactor={-2} polygonOffsetUnits={-2} />
      </mesh>
      {grid}
      {segs}
      {dots}
    </group>
  );
}

// ----------------------------------------------------------------------------
// RARE HQ HELICOPTER — flies in from the sky, lands on the rooftop helipad, sits
// for a few seconds, then takes off and leaves. After a full visit it stays away
// for at least ~2 minutes (per the brief: "I do not want to see another one for
// at least 2 minutes."). State machine:
//   away → incoming → descend → landed → ascend → outgoing → (≥2 min cooldown) …
// ----------------------------------------------------------------------------
// First appearance delay (seconds) after the world loads. Subsequent visits are
// always gated by a ≥120s cooldown below.
const HELI_FIRST_WAIT = 14;

function HQHelicopter({ h }) {
  const heli = useRef();
  const rotor = useRef();
  const tail = useRef();
  const navRef = useRef();

  // Key flight points in the HQ's LOCAL space (pad centre is the local x/z origin).
  const pts = useMemo(() => {
    const padY = h + 1.95;     // resting height — skids sit just above the helipad
    const hoverY = h + 6.5;    // hover height above the pad
    const farY = h + 13;       // cruise-in / depart altitude
    return {
      PAD: new THREE.Vector3(0, padY, 0),
      HOVER: new THREE.Vector3(0, hoverY, 0),
      IN: new THREE.Vector3(24, farY, 20),         // arrives from this corner of the sky
      OUT: new THREE.Vector3(-26, farY + 2, -18),  // departs toward the opposite side
    };
  }, [h]);

  const seg = useMemo(() => ({ incoming: 6.5, descend: 3.2, ascend: 3.0, outgoing: 6.5 }), []);
  // landed timing is randomized each visit (set on touchdown): blades spin DOWN to a
  // full stop, the chopper RESTS for a good while, then the blades spin back UP before
  // it lifts off. rotorPower is eased so spin-up/down look mechanical, not instant.
  const st = useRef({
    mode: "away", t: 0, wait: HELI_FIRST_WAIT + Math.random() * 8, spin: 0, rotorPower: 0,
    spinDown: 4, rest: 18, spinUp: 3, landedTotal: 25,
  });

  const easeInOut = (x) => (x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2);
  const easeOut = (x) => 1 - Math.pow(1 - x, 3);
  const easeIn = (x) => x * x * x;
  const tmpA = useRef(new THREE.Vector3());

  useFrame((_, delta) => {
    const dt = Math.min(delta, 0.05);
    const s = st.current;
    const g = heli.current;
    if (!g) return;

    // --- rotor power target by phase (eased so the blades spin up / wind down) ---
    let rotorTarget;
    if (s.mode === "away") rotorTarget = 0;
    else if (s.mode === "landed") rotorTarget = s.t < s.spinDown + s.rest ? 0 : 1; // stop, then spin back up
    else rotorTarget = 1; // incoming / descend / ascend / outgoing run at full
    s.rotorPower += (rotorTarget - s.rotorPower) * Math.min(1, dt * 1.3);
    const effPower = s.rotorPower < 0.04 ? 0 : s.rotorPower; // truly STOP when wound down
    s.spin += dt * effPower * 34;
    if (rotor.current) rotor.current.rotation.y = s.spin;
    if (tail.current) tail.current.rotation.x = s.spin * 1.4;
    if (navRef.current) navRef.current.material.opacity = s.mode === "away" ? 0 : (Math.sin(s.t * 6) > 0 ? 1 : 0.12);

    s.t += dt;
    g.visible = s.mode !== "away";

    const pose = (from, to, p, pitch) => {
      g.position.copy(tmpA.current.copy(from)).lerp(to, p);
      const dx = to.x - from.x;
      const dz = to.z - from.z;
      if (Math.abs(dx) + Math.abs(dz) > 0.001) g.rotation.y = Math.atan2(dx, dz);
      g.rotation.x = pitch;
    };

    switch (s.mode) {
      case "away":
        g.position.copy(pts.IN);
        if (s.t >= s.wait) { s.t = 0; s.mode = "incoming"; }
        break;
      case "incoming": {
        const p = easeOut(Math.min(1, s.t / seg.incoming));
        pose(pts.IN, pts.HOVER, p, -0.16 * (1 - p));
        if (s.t >= seg.incoming) { s.t = 0; s.mode = "descend"; }
        break;
      }
      case "descend": {
        const p = easeInOut(Math.min(1, s.t / seg.descend));
        pose(pts.HOVER, pts.PAD, p, 0);
        if (s.t >= seg.descend) {
          s.t = 0;
          s.mode = "landed";
          // randomize this rest: wind-down, a long stop, then wind-up before takeoff
          s.spinDown = 3.5 + Math.random() * 2.5;   // ~3.5–6s for the blades to stop
          s.rest = 14 + Math.random() * 26;         // ~14–40s parked with rotor OFF
          s.spinUp = 2.8 + Math.random() * 2.2;     // ~2.8–5s to spin back up
          s.landedTotal = s.spinDown + s.rest + s.spinUp;
        }
        break;
      }
      case "landed":
        g.position.copy(pts.PAD);
        g.rotation.x = 0;
        if (s.t >= s.landedTotal) { s.t = 0; s.mode = "ascend"; }
        break;
      case "ascend": {
        const p = easeIn(Math.min(1, s.t / seg.ascend));
        pose(pts.PAD, pts.HOVER, p, 0);
        if (s.t >= seg.ascend) { s.t = 0; s.mode = "outgoing"; }
        break;
      }
      case "outgoing": {
        const p = easeIn(Math.min(1, s.t / seg.outgoing));
        pose(pts.HOVER, pts.OUT, p, -0.14 * p);
        if (s.t >= seg.outgoing) { s.t = 0; s.mode = "away"; s.wait = 120 + Math.random() * 60; }
        break;
      }
      default:
        break;
    }
  });

  const body = BRAND.charcoal;
  const accent = BRAND.yellow;
  return (
    <group ref={heli} visible={false} scale={0.82}>
      {/* fuselage */}
      <RoundedBox args={[1.25, 1.05, 2.5]} radius={0.34} smoothness={4} castShadow>
        <meshStandardMaterial color={body} roughness={0.45} metalness={0.3} />
      </RoundedBox>
      {/* yellow brand stripe */}
      <mesh position={[0, 0.05, 0.02]}>
        <boxGeometry args={[1.28, 0.18, 1.9]} />
        <meshStandardMaterial color={accent} emissive={accent} emissiveIntensity={0.18} roughness={0.4} metalness={0.2} />
      </mesh>
      {/* cockpit glass (nose toward +Z) */}
      <mesh position={[0, 0.12, 1.12]} rotation={[0.2, 0, 0]}>
        <sphereGeometry args={[0.62, 18, 14, 0, Math.PI * 2, 0, Math.PI / 1.7]} />
        <meshStandardMaterial color={"#9fd0e0"} transparent opacity={0.6} roughness={0.1} metalness={0.4} />
      </mesh>
      {/* tail boom (-Z) */}
      <mesh position={[0, 0.18, -1.95]} castShadow>
        <boxGeometry args={[0.28, 0.28, 1.7]} />
        <meshStandardMaterial color={body} roughness={0.45} metalness={0.3} />
      </mesh>
      {/* tail fin */}
      <mesh position={[0, 0.5, -2.7]} castShadow>
        <boxGeometry args={[0.1, 0.7, 0.5]} />
        <meshStandardMaterial color={accent} roughness={0.4} metalness={0.25} />
      </mesh>
      {/* tail rotor (spins around X) */}
      <group ref={tail} position={[0.2, 0.5, -2.78]}>
        <mesh><boxGeometry args={[0.05, 0.9, 0.08]} /><meshStandardMaterial color={"#C8CBD2"} metalness={0.5} roughness={0.3} /></mesh>
        <mesh rotation={[Math.PI / 2, 0, 0]}><boxGeometry args={[0.05, 0.9, 0.08]} /><meshStandardMaterial color={"#C8CBD2"} metalness={0.5} roughness={0.3} /></mesh>
      </group>
      {/* rotor mast */}
      <mesh position={[0, 0.7, 0.1]}>
        <cylinderGeometry args={[0.08, 0.08, 0.45, 10]} />
        <meshStandardMaterial color={"#9A9DA4"} metalness={0.5} roughness={0.3} />
      </mesh>
      {/* main rotor (spins around Y) */}
      <group ref={rotor} position={[0, 0.95, 0.1]}>
        <mesh><cylinderGeometry args={[0.12, 0.12, 0.12, 10]} /><meshStandardMaterial color={accent} metalness={0.4} roughness={0.3} /></mesh>
        {[0, Math.PI / 2].map((a, i) => (
          <mesh key={i} rotation={[0, a, 0]}>
            <boxGeometry args={[5.4, 0.05, 0.22]} />
            <meshStandardMaterial color={"#2F333B"} roughness={0.5} metalness={0.2} />
          </mesh>
        ))}
      </group>
      {/* skids */}
      {[-0.5, 0.5].map((x) => (
        <mesh key={`sk${x}`} position={[x, -0.66, 0]} castShadow>
          <boxGeometry args={[0.1, 0.1, 2.0]} />
          <meshStandardMaterial color={"#C8CBD2"} metalness={0.5} roughness={0.3} />
        </mesh>
      ))}
      {[-0.55, 0.55].map((z) => (
        <mesh key={`st${z}`} position={[0, -0.4, z]}>
          <boxGeometry args={[1.1, 0.08, 0.08]} />
          <meshStandardMaterial color={"#9A9DA4"} metalness={0.5} roughness={0.3} />
        </mesh>
      ))}
      {/* blinking belly nav light */}
      <mesh ref={navRef} position={[0, -0.5, 0.9]}>
        <sphereGeometry args={[0.08, 8, 8]} />
        <meshBasicMaterial color={"#FF3B3B"} transparent opacity={0} toneMapped={false} />
      </mesh>
    </group>
  );
}

export default function BuildingDetails({ b }) {
  const night = useNight();
  const w = Number(b.width) || 4;
  const d = Number(b.depth) || 4;
  const h = Number(b.height) || 6;
  const accent = b.accent || BRAND.yellow;

  switch (b.slug) {
    case "hq":
      return (
        <group>
          <mesh position={[0, h + 1.42, 0]} rotation={[-Math.PI / 2, 0, 0]}><ringGeometry args={[1.0, 1.25, 32]} /><meshStandardMaterial color={BRAND.white} side={THREE.DoubleSide} /></mesh>
          <mesh position={[-0.35, h + 1.43, 0]}><boxGeometry args={[0.12, 0.02, 0.8]} /><meshStandardMaterial color={BRAND.white} /></mesh>
          <mesh position={[0.35, h + 1.43, 0]}><boxGeometry args={[0.12, 0.02, 0.8]} /><meshStandardMaterial color={BRAND.white} /></mesh>
          <mesh position={[0, h + 1.43, 0]}><boxGeometry args={[0.7, 0.02, 0.12]} /><meshStandardMaterial color={BRAND.white} /></mesh>
          <AcUnit position={[w * 0.28, h + 1.0, d * 0.28]} />
          <AcUnit position={[-w * 0.3, h + 1.0, -d * 0.2]} s={0.8} />
          <Flag position={[w * 0.4, h + 1.0, -d * 0.4]} color={accent} />
          <HQHelicopter h={h} />
        </group>
      );
    case "branding":
      return (
        <group>
          {/* brand palette: paint-chip swatches mounted by the entrance */}
          {[BRAND.charcoal, BRAND.white, BRAND.yellow, BRAND.gray2].map((c, i) => (
            <mesh key={`sw${i}`} position={[-w * 0.32, h * 0.16 + i * 0.5, d / 2 + 0.07]}>
              <planeGeometry args={[0.44, 0.44]} />
              <meshStandardMaterial color={c} emissive={c} emissiveIntensity={0.08} roughness={0.6} polygonOffset polygonOffsetFactor={-3} polygonOffsetUnits={-3} />
            </mesh>
          ))}
        </group>
      );
    case "marketing": {
      // 5 abstract, multi-colour neon signs cantilevering off the dark tower top.
      const wallX = w / 2;
      const signs = [
        { angle: Math.PI * 0.08, anchorY: h * 0.82, proj: 1.9, color: "#FF2E97", shape: "rect", scale: 1.25 },
        { angle: Math.PI * 0.5, anchorY: h * 0.63, proj: 1.6, color: "#19E3E8", shape: "ring", scale: 1.1 },
        { angle: Math.PI * 0.28, anchorY: h * 0.73, proj: 1.8, color: BRAND.yellow, shape: "bars", scale: 1.2 },
        { angle: -Math.PI * 0.12, anchorY: h * 0.54, proj: 1.5, color: "#FF7A1A", shape: "blade", scale: 1.1 },
        { angle: Math.PI * 0.62, anchorY: h * 0.9, proj: 1.7, color: "#9B5CFF", shape: "diamond", scale: 1.05 },
        // two more on the previously-empty back sides (-X and -Z)
        { angle: Math.PI * 1.12, anchorY: h * 0.78, proj: 1.7, color: "#27E08A", shape: "rect", scale: 1.1 },
        { angle: Math.PI * 1.5, anchorY: h * 0.6, proj: 1.5, color: "#FF2E97", shape: "blade", scale: 1.05 },
      ];
      return (
        <group>
          {signs.map((sgn, i) => (
            <NeonSign key={i} wallX={wallX} {...sgn} />
          ))}
        </group>
      );
    }
    case "consulting": {
      // matches the terraced glass tower in Building.js (v1..v3)
      const v1h = h * 0.45, v2h = h * 0.4, v3h = h * 0.34;
      const v2Top = v1h + v2h;          // setback terrace roof
      const v3Top = v1h + v2h + v3h;    // top roof
      const v2FrontZ = -d * 0.12 + (d * 0.85) / 2 + 0.06;
      const heights = [0.5, 0.9, 1.3, 1.8];
      return (
        <group>
          {/* hero "growth graph" data-screen on the setback volume's front */}
          <group position={[-w * 0.12, v1h + v2h * 0.5, v2FrontZ]}>
            <GrowthGraph width={w * 0.66} height={v2h * 0.82} accent={accent} />
          </group>
          {/* rooftop bar-chart icon on the top terrace (kept — the user liked this) */}
          {heights.map((bh, i) => (
            <RoundedBox key={i} args={[0.46, bh, 0.46]} radius={0.04} smoothness={2} position={[w * 0.2 + (i - 1.5) * 0.56, v3Top + 0.22 + bh / 2, -d * 0.04]} castShadow>
              <meshStandardMaterial color={i === heights.length - 1 ? accent : "#E8E4DA"} roughness={0.5} />
            </RoundedBox>
          ))}
          {/* rooftop pie-chart on the setback terrace */}
          <group position={[-w * 0.5, v2Top + 0.22, -d * 0.35]}>
            <mesh position={[0, 0.5, 0]}><cylinderGeometry args={[0.05, 0.07, 1.0, 8]} /><meshStandardMaterial color={"#E8E4DA"} /></mesh>
            <mesh position={[0, 1.15, 0]} rotation={[Math.PI / 2.5, 0, 0]}><cylinderGeometry args={[0.5, 0.5, 0.12, 28, 1, false, 0, Math.PI * 1.25]} /><meshStandardMaterial color={accent} emissive={accent} emissiveIntensity={night ? 0.4 : 0.1} roughness={0.5} /></mesh>
            <mesh position={[0, 1.15, 0]} rotation={[Math.PI / 2.5, 0, 0]}><cylinderGeometry args={[0.5, 0.5, 0.1, 28, 1, false, Math.PI * 1.25, Math.PI * 0.75]} /><meshStandardMaterial color={"#46D6E0"} emissive={"#46D6E0"} emissiveIntensity={night ? 0.5 : 0.12} roughness={0.5} /></mesh>
          </group>
          {/* glowing data-beacon spire on the top terrace */}
          <mesh position={[w * 0.2, v3Top + 0.2, -d * 0.04]}>
            <cylinderGeometry args={[0.04, 0.06, 1.4, 6]} />
            <meshStandardMaterial color={BRAND.charcoal} metalness={0.4} roughness={0.4} />
          </mesh>
          <mesh position={[w * 0.2, v3Top + 0.95, -d * 0.04]}>
            <sphereGeometry args={[0.13, 12, 12]} />
            <meshStandardMaterial color={accent} emissive={accent} emissiveIntensity={night ? 1.2 : 0.5} toneMapped={false} />
          </mesh>
        </group>
      );
    }
    case "experiential":
      return null;
    case "contact":
      return <AnimatedBeacon h={h} w={w} accent={accent} />;
    case "drilldown": {
      const covers = [accent, BRAND.charcoal, "#C7C3B8", accent, BRAND.charcoal];
      return (
        <group>
          <Suspense fallback={null}>
            <DrillDownWallLogo w={w} d={d} h={h} />
          </Suspense>
          {/* corporate windows now live on the right (+X) wall, beside the door */}
          <DrillDownWindows w={w} d={d} h={h} />
          {/* peaked canopy roof */}
          <mesh position={[0, h + 0.5, 0]} castShadow><coneGeometry args={[w * 0.8, 1.2, 4]} rotation={[0, Math.PI / 4, 0]} /><meshStandardMaterial color={BRAND.charcoal} roughness={0.5} /></mesh>
          <mesh position={[0, h + 0.5, 0]} rotation={[0, Math.PI / 4, 0]} castShadow><coneGeometry args={[w * 0.82, 1.18, 4]} /><meshStandardMaterial color={accent} roughness={0.5} /></mesh>
          {/* spinning drill sculpture on top */}
          <AnimatedDrill y={h + 1.2} accent={accent} />
          {/* news ticker band */}
          <Ticker w={w} y={h * 0.78} d={d} />
          {/* magazine rack with colorful covers */}
          <group position={[-w / 2 - 0.5, 0, d * 0.1]}>
            {covers.map((c, i) => (<mesh key={i} position={[0, 0.6 + i * 0.02, (i - 2) * 0.45]} rotation={[0, 0, -0.15]}><boxGeometry args={[0.06, 1.0, 0.4]} /><meshStandardMaterial color={c} roughness={0.7} /></mesh>))}
            <mesh position={[0, 0.05, 0]}><boxGeometry args={[0.5, 0.1, 2.2]} /><meshStandardMaterial color={BRAND.charcoal} /></mesh>
          </group>
          {/* globe on a stand */}
          <group position={[w / 2 + 0.5, 0, -d * 0.1]}>
            <mesh position={[0, 0.5, 0]}><cylinderGeometry args={[0.06, 0.08, 1.0, 8]} /><meshStandardMaterial color={BRAND.charcoal} /></mesh>
            <mesh position={[0, 1.1, 0]}><sphereGeometry args={[0.45, 16, 12]} /><meshStandardMaterial color={"#5B86A6"} roughness={0.6} /></mesh>
            <mesh position={[0, 1.1, 0]} rotation={[0.4, 0, 0]}><torusGeometry args={[0.52, 0.03, 8, 24]} /><meshStandardMaterial color={accent} /></mesh>
          </group>
          {/* paper stacks on counter */}
          {[-1, 1].map((x) => (<group key={x} position={[x * 1.4, h * 0.5 + 0.2, d / 2 + 0.45]}>{[0, 1, 2].map((k) => (<mesh key={k} position={[0, k * 0.12, 0]} rotation={[0, (k - 1) * 0.08, 0]}><boxGeometry args={[0.7, 0.1, 0.5]} /><meshStandardMaterial color={k % 2 ? BRAND.white : "#EDEDED"} roughness={0.9} /></mesh>))}</group>))}
        </group>
      );
    }
    default:
      return null;
  }
}
